/**
 * Aspect — Scene-view (overhead / fly) camera, Unity-Editor style
 * --------------------------------------------------------------
 * A free-fly inspection camera that complements the first-person walk mode.
 * Toggle it on to lift the camera above the home and look down. Navigation
 * mirrors the Unity Editor Scene view:
 *   - Hold RIGHT mouse button + drag to look around (yaw/pitch).
 *   - While RMB is held: W/A/S/D fly forward/left/back/right along the view,
 *     Q/E move down/up, Shift flies faster.
 *   - Hold MIDDLE mouse button + drag to pan (slide the view) like a hand tool.
 *   - Mouse wheel dollies the camera along its view direction (zoom).
 *   - Touch (phones): one-finger drag rotates, pinch zooms, two-finger drag pans.
 *
 * While enabled, FirstPersonControls is suspended so the two never fight over
 * the camera. Disabling restores first-person walk.
 */

import * as pc from "playcanvas";

const DEG = Math.PI / 180;
const LOOK_SENS = 0.16;
const TOUCH_LOOK_SENS = 0.14;
const MOVE_SPEED = 6.0;
const SPRINT_MULT = 2.5;
const WHEEL_STEP = 0.01;
const TOUCH_PINCH_ZOOM = 0.018;
const PAN_SENS = 0.0016;
const TOUCH_PAN_SENS = 0.0018;
const PITCH_LIMIT = 89;
const TRANSITION_SEC = 0.95;
const EYE_HEIGHT = 1.6;
const PLAYER_RADIUS = 0.28;
const OVERHEAD_AZIMUTH_DEG = 38;
const OVERHEAD_ELEVATION_DEG = 52;

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerpAngle(from, to, t) {
  let diff = ((to - from + 540) % 360) - 180;
  return from + diff * t;
}

function smoothstep(t) {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

function prefersTouch() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(pointer: coarse)")?.matches) return true;
  return (navigator.maxTouchPoints ?? 0) > 0;
}

export class SceneViewControls {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} sceneManager
   * @param {{ canvas?: HTMLCanvasElement, fp?: import('./first_person_controls.js').FirstPersonControls }} [opts]
   */
  constructor(app, camera, sceneManager, opts = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = sceneManager;
    this.fp = opts.fp ?? null;
    this.canvas = opts.canvas ?? app.graphicsDevice?.canvas ?? null;

    this.enabled = false;
    this.flying = false; // RMB held (look + WASD fly)
    this.panning = false; // MMB held (slide the view)
    this.yaw = 0;
    this.pitch = 0;
    this._transitioning = false;
    this._transition = null;
    /** Walk pose saved when lifting into overhead (restored on the way down). */
    this._savedWalk = null;
    this.touchUi = prefersTouch();
    /** Azimuth around the home centre (degrees). Used by cinematic reel orbit. */
    this._orbitAzimuth = OVERHEAD_AZIMUTH_DEG;

    // Touch state for rotate / pinch-zoom / two-finger pan.
    this._touches = new Map();
    this._orbitLast = null;
    this._pinchLastDist = null;
    this._pinchLastMid = null;

    this._onMouseDown = (e) => this._mouseDown(e);
    this._onMouseUp = (e) => this._mouseUp(e);
    this._onMouseMove = (e) => this._mouseMove(e);
    this._onWheel = (e) => this._wheel(e);
    this._onContextMenu = (e) => e.preventDefault();
    this._onBlur = () => {
      this.flying = false;
      this.panning = false;
      this._clearTouches();
    };
    this._onTouchStart = (e) => this._touchStart(e);
    this._onTouchMove = (e) => this._touchMove(e);
    this._onTouchEnd = (e) => this._touchEnd(e);

    this._bind();
    this.app.on("update", (dt) => this._update(dt));
    this.app.on("scene:room-loaded", () => {
      if (this.enabled) {
        const p = this.camera.getLocalPosition();
        this._snapOverheadAt(p.x, p.z);
      }
    });
  }

  get isTransitioning() {
    return this._transitioning;
  }

  /** True while overhead is active or animating into/out of it (drives wall fade). */
  get overheadActive() {
    return this.enabled || this._transitioning;
  }

  _bind() {
    if (this.canvas) {
      this.canvas.addEventListener("mousedown", this._onMouseDown);
      this.canvas.addEventListener("contextmenu", this._onContextMenu);
      this.canvas.addEventListener("wheel", this._onWheel, { passive: false });
    }
    window.addEventListener("mouseup", this._onMouseUp);
    window.addEventListener("mousemove", this._onMouseMove);
    window.addEventListener("blur", this._onBlur);

    // Document-level touch so HUD chrome / mode switch doesn't block orbit/pinch.
    if (this.touchUi) {
      const opts = { passive: false, capture: true };
      document.addEventListener("touchstart", this._onTouchStart, opts);
      document.addEventListener("touchmove", this._onTouchMove, opts);
      document.addEventListener("touchend", this._onTouchEnd, opts);
      document.addEventListener("touchcancel", this._onTouchEnd, opts);
    }
  }

  _uiBlocksTouch(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        "button, a, input, textarea, select, .mode-segment, .hud-btn, .touch-gyro-btn, .buy-soon, [data-overlay='hover-buy'], [data-overlay='buy-soon']",
      ),
    );
  }

  _clearTouches() {
    this._touches.clear();
    this._orbitLast = null;
    this._pinchLastDist = null;
    this._pinchLastMid = null;
  }

  _dolly(step) {
    const f = this.camera.forward;
    this.camera.translate(f.x * step, f.y * step, f.z * step);
  }

  _panByScreenDelta(dx, dy, sens = PAN_SENS) {
    const pos = this.camera.getLocalPosition();
    const floorY = this.scene.room?.floorY ?? 0;
    const scale = sens * Math.max(2, Math.abs(pos.y - floorY));
    const r = this.camera.right;
    const u = this.camera.up;
    const mx = -dx * scale;
    const my = dy * scale;
    this.camera.translate(r.x * mx + u.x * my, r.y * mx + u.y * my, r.z * mx + u.z * my);
  }

  _touchStart(ev) {
    if (!this.enabled || this._transitioning) return;
    if (this._uiBlocksTouch(ev.target)) return;
    for (const t of ev.changedTouches ?? []) {
      this._touches.set(t.identifier, { x: t.clientX, y: t.clientY });
    }
    this._syncTouchGesture();
    if (this._touches.size > 0) ev.preventDefault();
  }

  _touchMove(ev) {
    if (!this.enabled || this._transitioning) return;
    if (this._touches.size === 0) return;
    for (const t of ev.changedTouches ?? []) {
      if (!this._touches.has(t.identifier)) continue;
      this._touches.set(t.identifier, { x: t.clientX, y: t.clientY });
    }

    const pts = [...this._touches.values()];
    if (pts.length >= 2) {
      const a = pts[0];
      const b = pts[1];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (this._pinchLastDist != null && this._pinchLastMid) {
        // Pinch together → zoom in; spread → zoom out.
        const dDist = dist - this._pinchLastDist;
        this._dolly(-dDist * TOUCH_PINCH_ZOOM);
        this._panByScreenDelta(
          mid.x - this._pinchLastMid.x,
          mid.y - this._pinchLastMid.y,
          TOUCH_PAN_SENS,
        );
      }
      this._pinchLastDist = dist;
      this._pinchLastMid = mid;
      this._orbitLast = null;
    } else if (pts.length === 1 && this._orbitLast) {
      const p = pts[0];
      this.yaw -= (p.x - this._orbitLast.x) * TOUCH_LOOK_SENS;
      this.pitch -= (p.y - this._orbitLast.y) * TOUCH_LOOK_SENS;
      this._applyRotation();
      this._orbitLast = { x: p.x, y: p.y };
    }

    ev.preventDefault();
  }

  _touchEnd(ev) {
    for (const t of ev.changedTouches ?? []) {
      this._touches.delete(t.identifier);
    }
    this._syncTouchGesture();
  }

  _syncTouchGesture() {
    const pts = [...this._touches.values()];
    if (pts.length >= 2) {
      const a = pts[0];
      const b = pts[1];
      this._pinchLastDist = Math.hypot(a.x - b.x, a.y - b.y);
      this._pinchLastMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this._orbitLast = null;
    } else if (pts.length === 1) {
      this._pinchLastDist = null;
      this._pinchLastMid = null;
      this._orbitLast = { x: pts[0].x, y: pts[0].y };
    } else {
      this._pinchLastDist = null;
      this._pinchLastMid = null;
      this._orbitLast = null;
    }
  }

  toggle() {
    if (this._transitioning) return;
    this.enabled ? this.disable() : this.enable();
  }

  /**
   * Rotate the dollhouse camera around the home centre. No-op unless overhead
   * is fully active (not mid-transition).
   * @param {number} deltaDeg
   */
  orbitBy(deltaDeg) {
    if (!this.enabled || this._transitioning) return;
    this._orbitAzimuth = ((this._orbitAzimuth + (Number(deltaDeg) || 0)) % 360 + 360) % 360;
    const bounds = this.scene.room?.bounds;
    const pos = this.camera.getLocalPosition();
    const cx = bounds ? (bounds.min[0] + bounds.max[0]) / 2 : pos.x;
    const cz = bounds ? (bounds.min[2] + bounds.max[2]) / 2 : pos.z;
    this._snapOverheadAt(cx, cz);
  }

  enable() {
    if (this.enabled || this._transitioning) return;

    const pos = this.camera.getLocalPosition();
    const from = {
      x: pos.x,
      y: pos.y,
      z: pos.z,
      pitch: this.fp?.pitch ?? this.pitch,
      yaw: this.fp?.yaw ?? this.yaw,
    };
    this._savedWalk = { x: pos.x, z: pos.z, yaw: from.yaw, pitch: from.pitch };
    const overhead = this._computeOverheadPose(pos.x, pos.z);

    this.fp?.setEnabled?.(false);
    this.app.fire("fp:release-lock", {});
    this.app.fire("cam:mode-changed", { mode: "overhead" });

    this._startTransition(from, overhead, () => {
      this.enabled = true;
      this.pitch = overhead.pitch;
      this.yaw = overhead.yaw;
    });
  }

  /** Drop overhead instantly — no fly-to-landing. Used by Revive Enter Tour. */
  exitImmediate() {
    this._transitioning = false;
    this._transition = null;
    this.enabled = false;
    this.flying = false;
    this.panning = false;
    this._clearTouches();
    this._savedWalk = null;
    this.app.fire("cam:mode-changed", { mode: "fp" });
  }

  disable() {
    if ((!this.enabled && !this._transitioning) || this._transitioning) return;

    const fromPos = this.camera.getLocalPosition();
    const from = {
      x: fromPos.x,
      y: fromPos.y,
      z: fromPos.z,
      pitch: this.pitch,
      yaw: this.yaw,
    };
    const land = this._resolveWalkLanding();
    const walk = {
      x: land.x,
      y: land.eyeY,
      z: land.z,
      pitch: this._savedWalk?.pitch ?? 0,
      yaw: this._savedWalk?.yaw ?? this.yaw,
    };

    this.enabled = false;
    this.flying = false;
    this.panning = false;
    this._clearTouches();
    this.fp?.setEnabled?.(false);
    this.app.fire("cam:mode-changed", { mode: "fp" });

    this._startTransition(from, walk, () => {
      this._orbitAzimuth = OVERHEAD_AZIMUTH_DEG;
      this.fp?.adoptPose?.(new pc.Vec3(walk.x, walk.y, walk.z), walk.yaw, walk.pitch);
      this.fp?.setEnabled?.(true);
      this._savedWalk = null;
    });
  }

  /** Dollhouse camera pose looking at a floor point (x, z). */
  _computeOverheadPose(focusX, focusZ) {
    const room = this.scene.room;
    const floorY = room?.floorY ?? 0;
    const bounds = room?.bounds;
    let span = 8;
    if (bounds) {
      span = Math.max(bounds.max[0] - bounds.min[0], bounds.max[2] - bounds.min[2]);
    }

    const lookY = floorY + (room?.dimensions?.height ?? 2.8) * 0.38;
    const azimuth = (this._orbitAzimuth ?? OVERHEAD_AZIMUTH_DEG) * DEG;
    const elevation = OVERHEAD_ELEVATION_DEG * DEG;
    const dist = Math.max(5.5, span * 0.9);
    const y = floorY + dist * Math.sin(elevation);
    const horiz = dist * Math.cos(elevation);
    const x = focusX + horiz * Math.sin(azimuth);
    const z = focusZ + horiz * Math.cos(azimuth);

    const dx = focusX - x;
    const dy = lookY - y;
    const dz = focusZ - z;
    const horizLen = Math.hypot(dx, dz) || 1e-6;
    const yaw = (Math.atan2(-dx, -dz) * 180) / Math.PI;
    const pitch = pc.math.clamp((Math.atan2(dy, horizLen) * 180) / Math.PI, -PITCH_LIMIT, PITCH_LIMIT);

    return { x, y, z, pitch, yaw };
  }

  _snapOverheadAt(focusX, focusZ) {
    const o = this._computeOverheadPose(focusX, focusZ);
    this.camera.setLocalPosition(o.x, o.y, o.z);
    this.pitch = o.pitch;
    this.yaw = o.yaw;
    this._applyRotation();
  }

  /** Floor XZ under the overhead crosshair; resolved for walls and furniture. */
  _resolveWalkLanding() {
    const room = this.scene.room;
    const floorY = room?.floorY ?? 0;
    const eyeY = floorY + EYE_HEIGHT;
    const pos = this.camera.getLocalPosition();
    const fwd = this.camera.forward;
    let x = pos.x;
    let z = pos.z;

    const planeY = floorY + 0.02;
    if (Math.abs(fwd.y) > 1e-5) {
      const t = (planeY - pos.y) / fwd.y;
      if (t > 0) {
        x = pos.x + fwd.x * t;
        z = pos.z + fwd.z * t;
      }
    }

    const bounds = room?.bounds;
    if (bounds) {
      x = pc.math.clamp(x, bounds.min[0] + PLAYER_RADIUS, bounds.max[0] - PLAYER_RADIUS);
      z = pc.math.clamp(z, bounds.min[2] + PLAYER_RADIUS, bounds.max[2] - PLAYER_RADIUS);
    }

    const collision = this.scene.wallCollision;
    if (collision?.segments?.length || collision?.obstacles?.length) {
      const resolved = collision.resolve(x, z, x, z, PLAYER_RADIUS);
      x = resolved.x;
      z = resolved.z;
    }

    return { x, z, eyeY };
  }

  _startTransition(from, to, onComplete) {
    this._transitioning = true;
    this._transition = {
      from,
      to,
      elapsed: 0,
      duration: TRANSITION_SEC,
      onComplete,
    };
  }

  _tickTransition(dt) {
    const tr = this._transition;
    if (!tr) return false;

    tr.elapsed += dt;
    const u = smoothstep(Math.min(1, tr.elapsed / tr.duration));

    const x = lerp(tr.from.x, tr.to.x, u);
    const y = lerp(tr.from.y, tr.to.y, u);
    const z = lerp(tr.from.z, tr.to.z, u);
    this.pitch = lerp(tr.from.pitch, tr.to.pitch, u);
    this.yaw = lerpAngle(tr.from.yaw, tr.to.yaw, u);

    this.camera.setLocalPosition(x, y, z);
    this._applyRotation();

    if (tr.elapsed >= tr.duration) {
      this._transitioning = false;
      this._transition = null;
      tr.onComplete?.();
    }
    return true;
  }

  /** @deprecated use _snapOverheadAt */
  _frameOverhead() {
    const bounds = this.scene.room?.bounds;
    const cx = bounds ? (bounds.min[0] + bounds.max[0]) / 2 : 0;
    const cz = bounds ? (bounds.min[2] + bounds.max[2]) / 2 : 0;
    this._snapOverheadAt(cx, cz);
  }

  _applyRotation() {
    this.pitch = pc.math.clamp(this.pitch, -PITCH_LIMIT, PITCH_LIMIT);
    this.camera.setLocalEulerAngles(this.pitch, this.yaw, 0);
  }

  _mouseDown(e) {
    if (!this.enabled || this._transitioning) return;
    if (e.button === 2) this.flying = true; // right button: look + fly
    if (e.button === 1) {
      this.panning = true; // middle button: pan the view
      e.preventDefault();
    }
  }

  _mouseUp(e) {
    if (e.button === 2) this.flying = false;
    if (e.button === 1) this.panning = false;
  }

  _mouseMove(e) {
    if (!this.enabled || this._transitioning) return;
    if (this.flying) {
      this.yaw -= e.movementX * LOOK_SENS;
      this.pitch -= e.movementY * LOOK_SENS;
      this._applyRotation();
    } else if (this.panning) {
      this._panByScreenDelta(e.movementX, e.movementY, PAN_SENS);
    }
  }

  _wheel(e) {
    if (!this.enabled || this._transitioning) return;
    e.preventDefault();
    // Dolly along the view direction; wheel-up (negative deltaY) zooms in.
    this._dolly(-e.deltaY * WHEEL_STEP);
  }

  _update(dt) {
    if (this._tickTransition(dt)) return;
    if (!this.enabled || !this.flying) return;
    const kb = this.app.keyboard;
    if (!kb) return;

    const fwd = kb.isPressed(pc.KEY_W) || kb.isPressed(pc.KEY_UP);
    const back = kb.isPressed(pc.KEY_S) || kb.isPressed(pc.KEY_DOWN);
    const left = kb.isPressed(pc.KEY_A) || kb.isPressed(pc.KEY_LEFT);
    const right = kb.isPressed(pc.KEY_D) || kb.isPressed(pc.KEY_RIGHT);
    const down = kb.isPressed(pc.KEY_Q);
    const up = kb.isPressed(pc.KEY_E);
    const sprint = kb.isPressed(pc.KEY_SHIFT);
    if (!fwd && !back && !left && !right && !down && !up) return;

    const f = this.camera.forward;
    const r = this.camera.right;
    let dx = 0;
    let dy = 0;
    let dz = 0;
    if (fwd) ((dx += f.x), (dy += f.y), (dz += f.z));
    if (back) ((dx -= f.x), (dy -= f.y), (dz -= f.z));
    if (right) ((dx += r.x), (dy += r.y), (dz += r.z));
    if (left) ((dx -= r.x), (dy -= r.y), (dz -= r.z));
    if (up) dy += 1;
    if (down) dy -= 1;

    const len = Math.hypot(dx, dy, dz) || 1;
    const speed = MOVE_SPEED * (sprint ? SPRINT_MULT : 1) * dt;
    this.camera.translate((dx / len) * speed, (dy / len) * speed, (dz / len) * speed);
  }

  destroy() {
    if (this.canvas) {
      this.canvas.removeEventListener("mousedown", this._onMouseDown);
      this.canvas.removeEventListener("contextmenu", this._onContextMenu);
      this.canvas.removeEventListener("wheel", this._onWheel);
    }
    window.removeEventListener("mouseup", this._onMouseUp);
    window.removeEventListener("mousemove", this._onMouseMove);
    window.removeEventListener("blur", this._onBlur);
    if (this.touchUi) {
      const opts = { capture: true };
      document.removeEventListener("touchstart", this._onTouchStart, opts);
      document.removeEventListener("touchmove", this._onTouchMove, opts);
      document.removeEventListener("touchend", this._onTouchEnd, opts);
      document.removeEventListener("touchcancel", this._onTouchEnd, opts);
    }
  }
}

export default SceneViewControls;
